import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CheckCircle, MapPin, Sparkles, AlertTriangle, CalendarPlus, Briefcase, FileText, X, Ruler } from 'lucide-react';
import { api as defaultApi } from '../services/api';
import AddressAutocomplete, { AddressSuggestion } from './AddressAutocomplete';

/**
 * The one way to add a person — a customer or a lead — wherever the tap came from.
 *
 * Owner, Sep 18 2026: "whenever adding a client, however doing it, make sure
 * it asks the same questions … first, last, phone number, address, how they
 * heard about us … we don't need email." Plus what they want, so the next step
 * (a quote visit, a job, a plan) is obvious. Everything else waits for the job.
 *
 * Under the address it says where the yard sits against the service area and
 * what the drive costs, before anything is saved.
 */
export const HOW_HEARD = ['Referral', 'Nextdoor', 'Google', 'Facebook', 'Flyer', 'Walk-by', 'Other'];
export const WANTS = [
  { key: 'mowing', label: 'Mowing' },
  { key: 'plan', label: 'Lawn plan' },
  { key: 'project', label: 'A project' },
  { key: 'unsure', label: 'Not sure yet' },
];

export interface IntakeValues {
  first_name: string; last_name: string; phone: string; email: string;
  address: string; city: string; state: string; zip_code: string; lat: number | null; lng: number | null;
  how_heard: string; wants: string; notes: string;
}
export const emptyIntake: IntakeValues = {
  first_name: '', last_name: '', phone: '', email: '',
  address: '', city: '', state: 'TX', zip_code: '', lat: null, lng: null,
  how_heard: '', wants: '', notes: '',
};

export interface IntakeResult {
  customer?: any; property?: any; area?: any; turf?: any; lead?: any;
}

type Api = { get: (url: string, cfg?: any) => Promise<any>; post: (url: string, body?: any) => Promise<any> };

export default function CustomerIntake({
  mode = 'customer', api = defaultApi, prefill, leadId = null, source, compact = false, ai = true,
  propertyExtras, extraFields, submitLabel, onSaved, onCancel, title,
}: {
  mode?: 'customer' | 'lead';
  api?: Api;
  prefill?: Partial<IntakeValues> & { name?: string };
  leadId?: number | null;
  source?: string;
  compact?: boolean;
  ai?: boolean;
  /** Extra columns for the yard (the Routes page sends price + minutes). */
  propertyExtras?: Record<string, any>;
  /** Extra inputs rendered above the buttons. */
  extraFields?: React.ReactNode;
  submitLabel?: string;
  onSaved: (out: IntakeResult) => void;
  onCancel?: () => void;
  title?: string;
}) {
  const [v, setV] = useState<IntakeValues>(() => fromPrefill(prefill));
  const [picked, setPicked] = useState<AddressSuggestion | null>(prefill?.lat && prefill?.lng ? ({ label: prefill.address || '', lat: prefill.lat, lng: prefill.lng } as any) : null);
  const [area, setArea] = useState<any>(null);
  const [turf, setTurf] = useState<any>(null);   // turf from the county map, the moment a pin lands
  const [saving, setSaving] = useState(false);
  const [dupe, setDupe] = useState<{ id: number; name: string } | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const navigate = useNavigate();
  const firstRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setV(fromPrefill(prefill)); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  , [prefill?.name, prefill?.first_name, prefill?.phone]);

  // Where does this yard sit against the service area? Asked the moment a pin lands.
  useEffect(() => {
    let dropped = false;
    if (!picked || picked.lat == null || picked.lng == null) { setArea(null); setTurf(null); return; }
    api.get('/service-area/check', { params: { lat: picked.lat, lng: picked.lng, address: picked.label || '' } })
      .then(r => { if (!dropped) { setArea(r.data); setTurf(r.data?.turf || null); } })
      .catch(() => { if (!dropped) { setArea(null); setTurf(null); } });
    return () => { dropped = true; };
  }, [picked, api]);

  const set = (k: keyof IntakeValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setV(p => ({ ...p, [k]: e.target.value }));

  async function fillWithAi() {
    if (!aiText.trim()) return;
    setAiBusy(true);
    try {
      const { data: d } = await api.post('/ai/create-customer', { text: aiText });
      setV(p => ({
        ...p,
        first_name: d.first_name || p.first_name, last_name: d.last_name || p.last_name,
        phone: d.phone || p.phone, email: d.email || p.email,
        how_heard: d.how_heard || p.how_heard,
        address: d.address || p.address, city: d.city || p.city, state: d.state || p.state || 'TX', zip_code: d.zip_code || p.zip_code,
        lat: null, lng: null,
        notes: [p.notes, d.notes, d.services?.length ? `Asked about: ${d.services.join(', ')}` : '', d.frequency ? `Wants: ${d.frequency}${d.preferred_day ? ` on ${d.preferred_day}` : ''}` : ''].filter(Boolean).join('\n'),
        wants: p.wants || (d.services?.some((s: string) => /mow/i.test(s)) ? 'mowing' : p.wants),
      }));
      setPicked(null);
      if (d.existing_customer_id) toast.warn('This looks like someone you already have — check before saving');
      else toast.success('Filled in — look it over, then save');
      setAiOpen(false);
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Could not read that'); }
    finally { setAiBusy(false); }
  }

  async function save(force = false) {
    if (!v.first_name.trim()) { toast.error('A first name is all we need to start'); firstRef.current?.focus(); return; }
    setSaving(true); setDupe(null);
    try {
      if (mode === 'lead') {
        const r = await api.post('/leads', {
          first_name: v.first_name.trim(), last_name: v.last_name.trim(), phone: v.phone.trim() || undefined, email: v.email.trim() || undefined,
          address: v.address.trim() || undefined, city: v.city || undefined, how_heard: v.how_heard || undefined, wants: v.wants || undefined,
          message: v.notes.trim() || undefined, source: source || 'manual',
        });
        onSaved({ lead: r.data.lead });
        return;
      }
      const body: any = {
        first_name: v.first_name.trim(), last_name: v.last_name.trim(),
        phone: v.phone.trim() || undefined, email: v.email.trim() || undefined,
        how_heard: v.how_heard || undefined, wants: v.wants || undefined, notes: v.notes.trim() || undefined,
        source: source || 'direct', lead_id: leadId || undefined, force: force || undefined,
      };
      if (v.address.trim()) {
        body.property = {
          label: null, address: v.address.trim(),   // no "Main property" placeholder — screens fall back to the address
          city: v.city || undefined, state: v.state || 'TX', zip_code: v.zip_code || undefined,
          lat: picked?.lat ?? undefined, lng: picked?.lng ?? undefined, ...(propertyExtras || {}),
        };
      }
      const r = await api.post('/customers', body);
      onSaved({ customer: r.data.customer, property: r.data.property || null, area: r.data.area || null, turf: r.data.turf || null });
    } catch (e: any) {
      const d = e?.response?.data;
      if (e?.response?.status === 409 && d?.existing) { setDupe(d.existing); }
      else toast.error(d?.message || 'Could not save that');
    } finally { setSaving(false); }
  }

  const pad = compact ? 'space-y-2.5' : 'space-y-3.5';
  const input = compact ? 'input text-sm py-2' : 'input';
  const chip = (on: boolean) => `px-3 py-1.5 rounded-full border text-sm ${on ? 'press bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-700 border-gray-300'}`;

  return (
    <div className={pad}>
      {title && (
        <div className="flex items-center justify-between">
          <p className="font-semibold text-gray-900">{title}</p>
          {onCancel && <button type="button" onClick={onCancel} aria-label="Close"><X className="h-5 w-5 text-gray-500" /></button>}
        </div>
      )}

      {ai && (
        <div className="rounded-xl border border-brand-200 bg-brand-50/60 p-2.5">
          <button type="button" onClick={() => setAiOpen(o => !o)} className="w-full flex items-center justify-between text-sm font-medium text-brand-700">
            <span className="flex items-center gap-1.5"><Sparkles className="h-4 w-4" /> Paste their text or your notes — the form fills itself</span>
            <span className="text-xs text-brand-500">{aiOpen ? 'hide' : 'open'}</span>
          </button>
          {aiOpen && (
            <div className="mt-2 space-y-2">
              <textarea value={aiText} onChange={e => setAiText(e.target.value)} rows={3} className="input text-sm"
                placeholder={'"Hi this is Kyle Watson, 16304 Lone Wolf Dr Leander, want weekly mowing, Natalie next door referred me, 512-555-0100"'} />
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">Nothing is saved until you press Save.</p>
                <button type="button" onClick={fillWithAi} disabled={!aiText.trim() || aiBusy} className="btn-primary text-xs py-1.5">{aiBusy ? 'Reading…' : 'Fill the form'}</button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="label-sm">First name *</span>
          <input ref={firstRef} value={v.first_name} onChange={set('first_name')} className={input} placeholder="Lauren" autoFocus autoCapitalize="words" autoComplete="off" />
        </label>
        <label className="block">
          <span className="label-sm">Last name</span>
          <input value={v.last_name} onChange={set('last_name')} className={input} placeholder="Bischoff" autoCapitalize="words" autoComplete="off" />
        </label>
      </div>

      <label className="block">
        <span className="label-sm">Mobile</span>
        <input value={v.phone} onChange={set('phone')} className={input} placeholder="(512) 555-0100" type="tel" inputMode="tel" autoComplete="off" />
      </label>

      <div>
        <span className="label-sm">Address of the yard</span>
        <AddressAutocomplete
          api={api}
          value={v.address}
          onChange={val => { setV(p => ({ ...p, address: val, lat: null, lng: null })); setPicked(null); }}
          onSelect={s => {
            setV(p => ({ ...p, address: s.address || s.label, city: s.city || p.city, state: s.state || 'TX', zip_code: s.zip || p.zip_code, lat: s.lat, lng: s.lng }));
            setPicked(s);
          }}
          placeholder="Start typing — nearest matches first"
          className={input}
        />
        {picked ? (
          <p className="mt-1 text-xs text-green-700 flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5" /> {picked.label}</p>
        ) : v.address.trim().length > 3 ? (
          <p className="mt-1 text-xs text-gray-500">No match picked — we'll save it as typed and pin it if the map knows it.</p>
        ) : null}
        {area && <AreaNote area={area} />}
        {turf && <TurfNote turf={turf} />}
      </div>

      <div>
        <span className="label-sm">How did they hear about us?</span>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {HOW_HEARD.map(h => (
            <button key={h} type="button" onClick={() => setV(p => ({ ...p, how_heard: p.how_heard === h ? '' : h }))} className={chip(v.how_heard === h)}>{h}</button>
          ))}
        </div>
      </div>

      <div>
        <span className="label-sm">What do they want?</span>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {WANTS.map(w => (
            <button key={w.key} type="button" onClick={() => setV(p => ({ ...p, wants: p.wants === w.key ? '' : w.key }))} className={chip(v.wants === w.key)}>{w.label}</button>
          ))}
        </div>
      </div>

      <label className="block">
        <span className="label-sm">Anything else? <span className="text-gray-500 font-normal">(gate code, the dog, what they said)</span></span>
        <input value={v.notes} onChange={set('notes')} className={input} placeholder="Optional" autoComplete="off" />
      </label>

      {extraFields}

      {dupe && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> {dupe.name} already has that number.</p>
          <p className="text-xs mt-0.5">Open them instead, or save anyway if it really is someone new.</p>
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={() => navigate(`/customers?profile=${dupe.id}`)} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold">Open {dupe.name.split(' ')[0]}</button>
            <button type="button" onClick={() => save(true)} disabled={saving} className="px-3 py-1.5 rounded-lg border border-amber-300 text-xs font-semibold">Save anyway</button>
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        {onCancel && <button type="button" onClick={onCancel} className="btn-secondary flex-shrink-0">Cancel</button>}
        <button type="button" onClick={() => save(false)} disabled={saving || !v.first_name.trim()} className="btn-primary flex-1 disabled:opacity-50">
          {saving ? 'Saving…' : submitLabel || (mode === 'lead' ? 'Add lead' : 'Save customer')}
        </button>
      </div>
      <p className="text-[11px] text-gray-500">Email is optional — they can add it themselves on their page. Price, schedule and yard details come with the first job.</p>
    </div>
  );
}

function fromPrefill(p?: Partial<IntakeValues> & { name?: string }): IntakeValues {
  const out = { ...emptyIntake, ...(p || {}) } as IntakeValues;
  if (p?.name && !p.first_name && !p.last_name) {
    const parts = String(p.name).trim().split(/\s+/);
    out.first_name = parts.shift() || '';
    out.last_name = parts.join(' ');
  }
  return out;
}

/** Where the yard sits against the service area, in one line. */
export function AreaNote({ area, className = '' }: { area: any; className?: string }) {
  if (!area || !area.known) return null;
  const out = !area.in_area && area.surcharge > 0;
  return (
    <p className={`mt-1.5 text-xs flex items-start gap-1.5 rounded-lg px-2.5 py-2 ${out ? 'bg-amber-50 text-amber-900 border border-amber-200' : 'bg-emerald-50 text-emerald-800 border border-emerald-200'} ${className}`}>
      <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
      <span>{area.label}</span>
    </p>
  );
}

/**
 * Turf from the county map, in one line: green when it is sure to 500 sq ft,
 * amber when he should measure on the visit (the reason is in the words).
 */
export function TurfNote({ turf, className = '' }: { turf: any; className?: string }) {
  if (!turf) return null;
  const tone = turf.sure ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : turf.known ? 'bg-amber-50 text-amber-900 border border-amber-200' : 'bg-gray-50 text-gray-700 border border-gray-200';
  return (
    <p className={`mt-1.5 text-xs flex items-start gap-1.5 rounded-lg px-2.5 py-2 ${tone} ${className}`}>
      <Ruler className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
      <span>{turf.label}{turf.sure && turf.band ? ` · within ${Number(turf.band).toLocaleString()} sq ft` : ''}{turf.flag && turf.known ? ' · on your list' : ''}</span>
    </p>
  );
}

/**
 * After a customer is saved: what happens next. A quote visit goes on the
 * calendar; a bid opens the new-bid sheet with them picked; a job opens the
 * job sheet for them; or nothing, for now.
 */
export function WhatsNext({ customer, property, area, turf, onDone }: { customer: any; property?: any; area?: any; turf?: any; onDone: () => void }) {
  const navigate = useNavigate();
  const first = String(customer?.first_name || customer?.name || '').split(' ')[0] || 'They';
  return (
    <div className="text-center space-y-3">
      <CheckCircle className="h-10 w-10 text-green-500 mx-auto" />
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{customer?.name || 'Customer'} is in.</h3>
        <p className="text-sm text-gray-500">What's next for {first}?</p>
      </div>
      {area && area.known && <AreaNote area={area} className="text-left" />}
      {turf && <TurfNote turf={turf} className="text-left" />}
      <div className="grid grid-cols-1 gap-2">
        <button onClick={() => { onDone(); navigate(`/calendar?quote=1&customer=${customer.id}`); }}
          className="press py-3 rounded-xl bg-brand-600 text-black font-semibold flex items-center justify-center gap-2">
          <CalendarPlus className="h-4 w-4" /> Book a quote visit
        </button>
        <button onClick={() => { onDone(); navigate('/quotes', { state: { prefillCustomerId: customer.id, prefillPropertyId: property?.id || null } }); }}
          className="py-3 rounded-xl border border-gray-300 bg-white text-gray-800 font-semibold flex items-center justify-center gap-2">
          <FileText className="h-4 w-4" /> Write a bid
        </button>
        <button onClick={() => { onDone(); navigate(`/calendar?customer=${customer.id}${property?.id ? `&property=${property.id}` : ''}`); }}
          className="py-3 rounded-xl border border-gray-300 bg-white text-gray-800 font-semibold flex items-center justify-center gap-2">
          <Briefcase className="h-4 w-4" /> Schedule a job
        </button>
        <button onClick={onDone} className="py-2.5 text-sm text-gray-500">Nothing right now</button>
      </div>
    </div>
  );
}
